<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>About Space Coast STEM</h2>
      </div>
    </section>

    <section class="content-section">
      <div class="container">

        <h2>What We Do</h2>
        <p>
          We provide refurbished computers and laptops to students in need across Brevard County, helping bridge the digital divide and ensuring every student has access to the technology they need for their education.
        </p>
      </div>
    </section>

    <section class="programs-section">
      <div class="container">
        <h2>Our Programs</h2>

        <!-- Current Program -->
        <div class="program-card current">
          <div class="program-badge">Current Focus</div>
          <h3>Laptop Program</h3>
          <p>
            Our primary focus is providing refurbished laptops to students who need them. We handle donations, sourcing, and refurbishment so teachers can focus on identifying students in need, and getting devices into their hands.
          </p>

          <router-link to="/theplan" class="program-link">Learn About The Plan →</router-link>
        </div>

        <!-- Future Programs -->
        <div class="future-programs">
          <h3>Future Programs</h3>
          <p> As we continue to grow, we hope to build industry partnerships and expand our offerings to include:</p>
        <br/>
          <ul>
            <li><strong>Coding Workshops</strong> - Hands-on programming workshops for students of all skill levels</li>
            <li><strong>STEM Mentorship</strong> - Connecting students with professionals in STEM fields</li>
            <li><strong>Hackathons & Competitions</strong> - Events where students can collaborate, innovate, and showcase their skills</li>
            <li><strong>Robotics & Maker Spaces</strong> - Access to tools and mentorship for robotics, electronics, and 3D printing</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="cta">
      <div class="container">
        <h2>Join Our Mission</h2>
        <p>Together, we can ensure every student has the tools they need to succeed</p>
        <div class="cta-buttons">
          <router-link to="/get-involved" class="btn btn-primary">Get Involved</router-link>
          <router-link to="/contact" class="btn btn-secondary">Contact Us</router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
// About view - static content
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

/* Content Section */
.content-section {
  padding: 5rem 2rem;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section h3 {
  font-size: 1.8rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--secondary-color, #3ba3d1);
  font-family: 'Poppins', sans-serif;
}

.content-section p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-light, #666666);
  margin-bottom: 1rem;
}

/* CTA Section */
.cta {
  background: var(--dark-bg, #1f3447);
  color: var(--white, #ffffff);
  padding: 5rem 2rem;
  text-align: center;
}

.cta h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

.cta p {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.btn-primary {
  background: var(--accent-color, #e94f3a);
  color: var(--white, #ffffff);
  border: none;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(233, 79, 58, 0.3);
}

.btn-secondary {
  border-color: var(--white, #ffffff);
  background: transparent;
  color: var(--white, #ffffff);
}

.btn-secondary:hover {
  background: var(--white, #ffffff);
  color: var(--primary-color, #2c4a6b);
}

/* Programs Section */
.programs-section {
  padding: 5rem 2rem;
  background: var(--light-bg, #f5f7fa);
}

.programs-section h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
  text-align: center;
}

.section-intro {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-light, #666666);
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.program-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.program-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.program-card.current {
  border: 2px solid var(--secondary-color, #3ba3d1);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.program-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--secondary-color, #3ba3d1);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.program-card.future .program-badge {
  background: var(--text-light, #666666);
}

.program-card h3 {
  font-size: 1.5rem;
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

.program-card p {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-light, #666666);
  margin-bottom: 1.5rem;
  flex-grow: 1;
}

.program-stats {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat {
  background: var(--light-bg, #f5f7fa);
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
}

.stat strong {
  display: block;
  font-size: 1.5rem;
  color: var(--secondary-color, #3ba3d1);
  margin-bottom: 0.25rem;
  font-family: 'Poppins', sans-serif;
}

.stat span {
  font-size: 0.85rem;
  color: var(--text-light, #666666);
  line-height: 1.3;
  display: block;
}

.program-link {
  display: inline-block;
  color: var(--secondary-color, #3ba3d1);
  font-weight: 600;
  text-decoration: none;
  transition: color 0.3s ease;
  margin-top: auto;
}

.program-link:hover {
  color: var(--primary-color, #2c4a6b);
}

/* Future Programs List */
.future-programs {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  max-width: 800px;
  margin: 0 auto;
}

.future-programs h3 {
  font-size: 1.5rem;
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  text-align: center;
}

.future-programs ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.future-programs li {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-light, #666666);
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  position: relative;
}

.future-programs li:before {
  content: "→";
  position: absolute;
  left: 0;
  color: var(--secondary-color, #3ba3d1);
  font-weight: bold;
}

.future-programs li strong {
  color: var(--primary-color, #2c4a6b);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero-content p {
    font-size: 1.1rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2,
  .cta h2,
  .programs-section h2 {
    font-size: 2rem;
  }

  .program-card,
  .future-programs {
    padding: 1.5rem;
  }

  .program-stats {
    flex-direction: column;
  }
}
</style>
